import { ArtistDTO, DownloadStatus, Song } from "../components/view/spotipitan/types";
import { getPlaylistPoster, getSongImage, Spotip17anFunctions } from "./api";

type SongFromDB = {
    song: Song;
    blob: Blob;
    poster: Blob;
}

const DB_NAME = 'spotip17an-db';
const DB_VERSION = 5;

export const createDB = async () => {
    const dbPromise = indexedDB.open(DB_NAME, DB_VERSION);

    const tryCreateObjectStore = (db: IDBDatabase, storeName: string) => {
        try {
            db.createObjectStore(storeName, { keyPath: "id" });
        } catch (e) {
            console.log("Error creating object store", e);
        }
    }

    dbPromise.onupgradeneeded = (event: any) => {
        const db = event.target.result as IDBDatabase;
        tryCreateObjectStore(db, "playlists");
        tryCreateObjectStore(db, "songPosters");
        tryCreateObjectStore(db, "songs");
    };
}

export const getSongFromDB = async (id: string): Promise<SongFromDB | null> => {
    return new Promise((resolve, reject) => {
        const db = indexedDB.open(DB_NAME, DB_VERSION);
        db.onsuccess = (event: any) => {
            try {
                const db = event.target.result as IDBDatabase;
                const transaction = db.transaction('songs', 'readwrite');
                const store = transaction?.objectStore('songs');
                const songRequest = store?.get(id);
                songRequest.onsuccess = (event: any) => {
                    const song = event.target.result as SongFromDB;
                    resolve(song);
                }
                songRequest.onerror = (event: any) => {
                    reject(new Error('Error getting song from DB'));
                }
            } catch (error) {
                reject(new Error('Error getting song from DB'));
            }
        }
        db.onerror = (event: any) => {
            reject(new Error('Error getting song from DB'));
        }
    });
}

export const saveSongToDB = async (song: Song, blob: Blob): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
        dbRequest.onsuccess = async (event: any) => {
            const posterSongPromise = getSongImage(song, true);

            const db = event.target.result as IDBDatabase;
            const transaction = db.transaction('songs', 'readwrite');
            const store = transaction?.objectStore('songs');
            store?.put({ song, blob, id: song.id.toString() });

            // save poster song
            const posterSong = await posterSongPromise;
            if (posterSong && posterSong.size > 0) {
                saveSongPosterToDB(song.id, posterSong);
            }

            resolve(true);
        }
        dbRequest.onerror = (event: any) => {
            reject(new Error('Error saving song to DB'));
        }
    });
}

export const savePlaylistToDB = async (playlist: ArtistDTO) => {
    return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
        dbRequest.onsuccess = async (event: any) => {
            const db = event.target.result as IDBDatabase;
            const transaction = db.transaction('playlists', 'readwrite');
            const store = transaction?.objectStore('playlists');
            store?.put({ ...playlist, id: playlist.id.toString() });

            // save poster song to BD if any
            if (playlist.poster_url) {
                const playlistPoster = await getPlaylistPoster(playlist);
                if (playlistPoster && playlistPoster.size > 0) {
                    saveSongPosterToDB("playlist_" + playlist.id.toString(), playlistPoster);
                }
            }

            resolve(true);
        }
        dbRequest.onerror = (event: any) => {
            reject(new Error('Error saving playlist to DB'));
        }
    });
}

export const getPlaylistsFromDB = async (): Promise<ArtistDTO[]> => {
    return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
        dbRequest.onsuccess = (event: any) => {
            const db = event.target.result as IDBDatabase;
            const transaction = db.transaction('playlists', 'readonly');
            const store = transaction?.objectStore('playlists');
            const playlists = store?.getAll();
            playlists.onsuccess = (event: any) => {
                resolve(event.target.result as ArtistDTO[]);
            }
            playlists.onerror = (event: any) => {
                reject(new Error('Error getting playlists from DB'));
            }
        }
        dbRequest.onerror = (event: any) => {
            reject(new Error('Error getting playlists from DB'));
        }
    });
}

export const getPlaylistFromDB = async (id: string): Promise<ArtistDTO | null> => {
    return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
        dbRequest.onsuccess = (event: any) => {
            try {

                const db = event.target.result as IDBDatabase;
                const transaction = db.transaction('playlists', 'readonly');
                const store = transaction?.objectStore('playlists');
                const playlist = store?.get(id);
                playlist.onsuccess = (event: any) => {
                    resolve(event.target.result as ArtistDTO);
                }
                playlist.onerror = (event: any) => {
                    reject(new Error('Error getting playlist from DB'));
                }
            } catch (error) {
                reject(new Error('Error getting playlist from DB'));
            }
        }
        dbRequest.onerror = (event: any) => {
            reject(new Error('Error getting playlist from DB'));
        }
    });
}

/**
 * Check if a song is in the DB
 * @param id - The id of the song
 * @returns True if the song is in the DB, false otherwise
 */
export const isSongInDB = async (id: string): Promise<boolean> => {
    try {
        const song = await getSongFromDB(id);
        return !!song;
    } catch (error) {
        return false;
    }
}

export const getPlaylistBdStatus = async (id: string | number): Promise<DownloadStatus> => {
    const isPlaylistInBd = await isPlaylistInDB(id.toString(), false);
    if (isPlaylistInBd) {
        const areSongsInBd = await isPlaylistInDB(id.toString(), true);
        if (!areSongsInBd) {
            return 'updated';
        } else {
            return 'downloaded';
        }
    }

    return undefined;
}

/**
 * Check if a playlist is in the DB
 * @param id - The id of the playlist
 * @returns True if the playlist is in the DB, false otherwise
 */
export const isPlaylistInDB = async (id: string, checkSongs = true): Promise<boolean> => {
    try {
        const playlist = await getPlaylistFromDB(id);
        const isInDB = playlist?.songs?.length;

        if (!isInDB) return false;

        if (!checkSongs) return true;

        const fetchedPlaylist: ArtistDTO = await Spotip17anFunctions.use("fetchPlaylist").call({ playlistId: id! });

        if (fetchedPlaylist.songs?.length !== playlist.songs!.length) return false;

        for (const song of fetchedPlaylist.songs!) {
            if (!(await isSongInDB(song.id))) return false;
        }

        return true;
    } catch {
        return false;
    }
}

export const saveSongPosterToDB = async (songId: string, poster: Blob) => {
    return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
        dbRequest.onsuccess = (event: any) => {
            const db = event.target.result as IDBDatabase;
            const transaction = db.transaction('songPosters', 'readwrite');
            const store = transaction?.objectStore('songPosters');
            store?.put({ poster, id: songId });
            resolve(true);
        }
        dbRequest.onerror = (event: any) => {
            reject(new Error('Error saving song poster to DB'));
        }
    });
}

export const getSongPosterFromDB = async (songId: string): Promise<Blob | null> => {
    return new Promise((resolve, reject) => {
        const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
        dbRequest.onsuccess = (event: any) => {
            try {
                const db = event.target.result as IDBDatabase;
                const transaction = db.transaction('songPosters', 'readonly');
                const store = transaction?.objectStore('songPosters');
                let songPoster: IDBRequest | undefined;
                songPoster = store?.get(songId);
                songPoster.onsuccess = (event: any) => {
                    resolve(event.target.result?.poster as Blob);
                }
                songPoster.onerror = (event: any) => {
                    reject(new Error('Error getting song poster from DB'));
                }
            } catch (e) {
                reject(new Error('Error getting song poster from DB'));
                return;
            }
        }
        dbRequest.onerror = (event: any) => {
            reject(new Error('Error getting song poster from DB'));
        }
    });
}

export const deleteSongFromIndexedDB = async (songId: string): Promise<boolean> => {
    return new Promise((resolve) => {
        try {
            const dbRequest = indexedDB.open(DB_NAME, DB_VERSION);
            dbRequest.onsuccess = (event: any) => {
                try {
                    const db = event.target.result as IDBDatabase;
                    const transaction = db.transaction(['songs', 'songPosters'], 'readwrite');
                    transaction.objectStore('songs').delete(songId);
                    transaction.objectStore('songPosters').delete(songId);
                    resolve(true);
                } catch {
                    resolve(false);
                }
            };
            dbRequest.onerror = () => resolve(false);
        } catch {
            resolve(false);
        }
    });
};


